// BEAM UI primitive — Button.
// Variants: primary (indigo) · secondary (neutral) · ghost (indigo text) ·
// danger (red text) · accent (copper — the SINGLE owner-gated CTA per screen).
// Sizes: sm | md | lg. Consumes only theme tokens.
import React from 'react';
import {
  ActivityIndicator,
  Pressable,
  StyleSheet,
  View,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import { theme } from '../../theme';
import { Text } from './Text';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'accent';
export type ButtonSize = 'sm' | 'md' | 'lg';

type ButtonProps = {
  title: string;
  onPress?: () => void;
  variant?: ButtonVariant;
  size?: ButtonSize;
  disabled?: boolean;
  loading?: boolean;
  fullWidth?: boolean;
  /** Optional leading element (e.g. an icon) rendered before the title. */
  left?: React.ReactNode;
  accessibilityLabel?: string;
  testID?: string;
  style?: StyleProp<ViewStyle>;
};

const SIZES: Record<ButtonSize, { height: number; paddingHorizontal: number; variant: 'small' | 'bodyStrong' | 'subhead' }> = {
  sm: { height: 36, paddingHorizontal: theme.spacing.md, variant: 'small' },
  md: { height: theme.layout.minTapTarget, paddingHorizontal: theme.spacing.lg, variant: 'bodyStrong' },
  lg: { height: 52, paddingHorizontal: theme.spacing.xl, variant: 'subhead' },
};

const FILLED = new Set<ButtonVariant>(['primary', 'accent']);

function bgFor(variant: ButtonVariant, pressed: boolean): string {
  switch (variant) {
    case 'primary':
      return pressed ? theme.palette.blueprint : theme.color.action.primary;
    case 'accent':
      return pressed ? theme.palette.copperWash : theme.color.action.accent;
    case 'secondary':
      return pressed ? theme.color.bg.subtle : theme.color.bg.surface;
    case 'ghost':
      return pressed ? theme.color.focusRing : 'transparent';
    case 'danger':
      return pressed ? theme.status.overdue.bg : 'transparent';
  }
}

function textColorFor(variant: ButtonVariant): 'onPrimary' | 'onAccent' | 'primary' | 'link' | 'danger' {
  switch (variant) {
    case 'primary':
      return 'onPrimary';
    case 'accent':
      return 'onAccent';
    case 'secondary':
      return 'primary';
    case 'ghost':
      return 'link';
    case 'danger':
      return 'danger';
  }
}

export function Button({
  title,
  onPress,
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  fullWidth = true,
  left,
  accessibilityLabel,
  testID,
  style,
}: ButtonProps) {
  const sz = SIZES[size];
  const isDisabled = disabled || loading;
  const border =
    variant === 'secondary'
      ? { borderWidth: theme.layout.hairline, borderColor: theme.color.border.strong }
      : null;

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel ?? title}
      accessibilityState={{ disabled: isDisabled, busy: loading }}
      testID={testID}
      onPress={onPress}
      disabled={isDisabled}
      style={({ pressed }) => [
        styles.base,
        { height: sz.height, paddingHorizontal: sz.paddingHorizontal, backgroundColor: bgFor(variant, pressed) },
        border,
        fullWidth && styles.fullWidth,
        isDisabled && styles.disabled,
        style,
      ]}
    >
      {loading ? (
        <ActivityIndicator color={FILLED.has(variant) ? theme.color.text.onPrimary : theme.color.action.primary} />
      ) : (
        <View style={styles.content}>
          {left}
          <Text variant={sz.variant} color={textColorFor(variant)}>
            {title}
          </Text>
        </View>
      )}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  base: {
    minHeight: theme.layout.minTapTarget,
    borderRadius: theme.radius.md,
    alignItems: 'center',
    justifyContent: 'center',
  },
  content: { flexDirection: 'row', alignItems: 'center', gap: theme.spacing.sm },
  fullWidth: { alignSelf: 'stretch' },
  disabled: { opacity: 0.45 },
});
